import React, { useMemo } from "react"
import { Theme, makeStyles } from "@material-ui/core"
import { escapeRegExp } from "lodash"

import { MessageViewModel } from "../api/message"
import { MessageMention } from "../api/message-response"

type Part = {
  text: string
  userId?: string
}

// The body carries a mention as the plain "@Name" that was typed; who that name pointed at
// travels beside it on the message. Splitting the two keeps the ids out of the draft, the
// editor and every preview, at the cost of this match on the way back in.
export const splitByMentions = (content: string, mentions: MessageMention[]): Part[] => {
  if (mentions.length === 0 || !content) return [{ text: content }]

  const userIdByName = new Map(mentions.map(({ name, userId }) => [name, userId]))
  // Longest first, so "@Anna Virtanen" wins over a colleague who is just "@Anna".
  const names = mentions.map(({ name }) => escapeRegExp(name)).sort((a, b) => b.length - a.length)
  const pattern = new RegExp(`@(${names.join("|")})`, "g")

  const parts: Part[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(content)) !== null) {
    if (match.index > cursor) parts.push({ text: content.slice(cursor, match.index) })

    parts.push({ text: match[0], userId: userIdByName.get(match[1]) })

    cursor = match.index + match[0].length
  }

  if (cursor < content.length) parts.push({ text: content.slice(cursor) })

  return parts
}

// Mentions of you still waiting to be looked at, oldest first. Not "unread": opening a chat
// marks it read at once, so having had the message on screen is what counts as having seen it.
export const unseenMentions = (messages: MessageViewModel[], currentUserId?: string, seenIds: string[] = []) =>
  messages.filter(
    (message) =>
      message.senderId !== currentUserId &&
      !seenIds.includes(message.id) &&
      message.mentions?.some((mention) => mention.userId === currentUserId)
  )

type Props = {
  content: string
  mentions?: MessageMention[]
  currentUserId?: string
}

// The body with its mentions marked — yours stands out from everyone else's.
const MentionedContent = ({ content, mentions = [], currentUserId }: Props) => {
  const classes = useStyles()
  const parts = useMemo(() => splitByMentions(content, mentions), [content, mentions])

  return (
    <>
      {parts.map((part, index) =>
        part.userId ? (
          <span key={index} className={part.userId === currentUserId ? classes.mentionMe : classes.mention}>
            {part.text}
          </span>
        ) : (
          part.text
        )
      )}
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  mention: {
    fontWeight: 600,
  },

  mentionMe: {
    fontWeight: 600,
    padding: "0 3px",
    borderRadius: 4,
    background: theme.palette.warning.light,
    color: theme.palette.text.primary,
  },
}))

export default MentionedContent
